import {useState} from 'react';

import {AppointmentHistoryContentType} from 'domain/usecases/private/my-appointments-flow';
import {QueryParamsEnum} from 'presentation/constants';
import {HistoryFilterEnum} from 'presentation/modules/myAppointments/constants/historyFilterEnum';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getAppointmentHistoryBookingType} from 'presentation/utils';
import {useMyAppointmentsListHistory} from '../../../../hooks/useMyAppointmentsListHistory';
import {FilterProps} from '../../types';
import {useTheme} from 'styled-components';
import {useContractStatus} from 'presentation/hooks';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {all, healthAppointment, teleHealth, emergency, scheduleType, surgery} =
	translations['pt-br'].commons;

export function useAppointmentsHistoryFilter(totalCount?: number) {
	const {isValid, isContractSuspended} = useContractStatus();
	const {isMobile} = useMediaQueries();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {bookingType} = SEARCH_PARAMS;
	const theme = useTheme();
	const [openDrawerState, setOpenDrawerState] = useState(false);
	const [selectedFilterState, setSelectedFilterState] = useState(bookingType);

	const {
		appointmentsListHistory,
		appointmentsListHistoryError,
		appointmentsListHistoryLoading,
		appointmentsListHistoryFilteredEmpty,
	} = useMyAppointmentsListHistory();

	const {congenereEligibility} = useCongenereEligibilityStore();

	const isFilterAvailable = (
		appointments: AppointmentHistoryContentType[],
		filterCategory: HistoryFilterEnum | string,
	): boolean => {
		if (!filterCategory) {
			return true;
		}

		return appointments.some(item => {
			const itemBookingType = getAppointmentHistoryBookingType({
				telemedicine: item.clinic.telemedicine,
				typeAttendance: item.typeAttendance,
			});
			return itemBookingType === filterCategory;
		});
	};

	const filters: FilterProps[] = appointmentsListHistory
		? [
				{
					label: all,
					category: '',
				},
				{
					label: surgery,
					category: HistoryFilterEnum.SURGERY,
				},
				{
					label: healthAppointment,
					category: HistoryFilterEnum.HEALTH,
				},
				{
					label: teleHealth,
					category: HistoryFilterEnum.TELEHEALTH,
				},
				{
					label: emergency,
					category: HistoryFilterEnum.EMERGENCY,
				},
			].filter(filter => isFilterAvailable(appointmentsListHistory, filter.category))
		: [];

	const hasFilters = filters.length > 2;
	const onlyOneAppointment = totalCount === 1;
	const appointmentFindResults = ` resultado${onlyOneAppointment ? '' : 's'} encontrado${onlyOneAppointment ? '' : 's'}`;

	const shouldHideComponent =
		appointmentsListHistoryError ||
		appointmentsListHistoryLoading ||
		appointmentsListHistoryFilteredEmpty ||
		!appointmentsListHistory;

	const shouldShowAppointmentButton = !isContractSuspended && isValid && !congenereEligibility;

	const handleSelectFilter = (type: HistoryFilterEnum | string) => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.BOOKING_TYPE, type);
			return prev;
		});
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleCloseDrawer = () => {
		setOpenDrawerState(false);
	};

	const listFilters = [
		{
			categoryTitle: scheduleType,
			categoryItemsList: filters.map(filter => ({
				active: selectedFilterState === filter.category,
				title: filter.label,
				onClick: () => setSelectedFilterState(filter.category),
			})),
		},
	];

	const handleApplyFilter = () => {
		handleSelectFilter(selectedFilterState);
		setOpenDrawerState(false);
	};

	const handleClearFilter = () => {
		setSelectedFilterState('');
		handleSelectFilter('');
		setOpenDrawerState(false);
	};

	return {
		hasFilters,
		openDrawerState,
		listFilters,
		handleOpenDrawer,
		handleCloseDrawer,
		handleApplyFilter,
		handleClearFilter,
		appointmentFindResults,
		shouldHideComponent,
		isValid,
		theme,
		isContractSuspended,
		isMobile,
		bookingType,
		shouldShowAppointmentButton,
	};
}
